import React from 'react';
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
    type VisibilityState,
} from '@tanstack/react-table';
import { TableHeaderCell } from './TableHeaderCell';

const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, label, [role="button"]';

/** True when `onRowClick` should skip a click: one meant for a nested control, or one from portalled
 * content (such as a Modal opened from a cell) that bubbled through the React tree but not the row. */
function isIgnoredRowClick(target: EventTarget | null, row: HTMLElement): boolean {
    if (!(target instanceof Node) || !row.contains(target)) return true;
    if (!(target instanceof Element)) return false;
    const interactive = target.closest(INTERACTIVE_SELECTOR);
    return interactive !== null && row.contains(interactive);
}

function isActivationKey(event: React.KeyboardEvent<HTMLElement>): boolean {
    return event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ');
}

const FOCUS_CLASSES =
    'cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mp-primary';

function rowClassName(isSelected: boolean, isExpanded: boolean, isClickable: boolean): string {
    let tint = 'hover:bg-mp-background';
    if (isSelected) tint = 'bg-mp-primary-content';
    else if (isClickable && isExpanded) tint = 'bg-mp-background';
    return `transition-colors ${tint} ${isClickable ? FOCUS_CLASSES : ''}`;
}

interface TableProps<T> {
    data: T[];
    columns: ColumnDef<T>[];
    className?: string;
    enableSorting?: boolean;
    sorting?: SortingState;
    onSortingChange?: (sorting: SortingState) => void;
    /** Heading shown when `data` is empty. Defaults to a generic message; pass a
     * specific one (e.g. "No products owned by this dealer yet.") for clarity. */
    emptyMessage?: string;
    /** Stable domain id per row; keys rows and cells so filtering keeps focus, and lets expansion match a row. */
    getRowId?: (row: T) => string;
    /** Id of the row to render `renderExpanded` beneath, or null for none. */
    expandedRowId?: string | null;
    renderExpanded?: (row: T) => React.ReactNode;
    /** Id of the row to mark selected (needs `getRowId`), or null for none. Styled apart from hover and expansion. */
    selectedRowId?: string | null;
    /** Live data cells read via `context.table.options.meta` without it becoming
     * a `columns` dep — recomputing `columns` remounts every cell. */
    meta?: Record<string, unknown>;
    /** Fires with a row's data on click, or Enter/Space on the focused row, ignoring clicks on a nested interactive
     * element (button, link, form control). Omit to leave rows non-interactive. */
    onRowClick?: (row: T) => void;
    /** Pixel widths by column id; when set the table uses fixed layout so sibling tables align. */
    columnWidths?: Record<string, number>;
    /** Column ids mapped to false are not rendered. */
    columnVisibility?: VisibilityState;
    /** False drops the table's own border and shadow, for a table that fills a `Card`. */
    framed?: boolean;
}

export const Table = <T,>({
    data,
    columns,
    className = '',
    enableSorting = true,
    sorting: externalSorting,
    onSortingChange,
    emptyMessage,
    getRowId,
    expandedRowId,
    renderExpanded,
    selectedRowId,
    meta,
    onRowClick,
    columnWidths,
    columnVisibility,
    framed = true,
}: TableProps<T>) => {
    const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);

    // Controlled mode requires both `sorting` and `onSortingChange`. If a consumer
    // supplies only one, warn so the surprising fallback to internal state is visible.
    if (process.env.NODE_ENV !== 'production') {
        if ((externalSorting !== undefined) !== (onSortingChange !== undefined)) {
            console.warn(
                '[Table] Controlled sorting requires BOTH `sorting` and `onSortingChange`. ' +
                    'Provide both for controlled mode, or neither for uncontrolled mode.',
            );
        }
    }

    const isControlled = onSortingChange !== undefined && externalSorting !== undefined;
    const sorting = isControlled ? externalSorting : internalSorting;

    // Treat an empty or whitespace-only `emptyMessage` as "not provided" so it
    // falls back to the generic heading + subtext rather than rendering blank.
    const hasCustomEmpty = !!emptyMessage?.trim();

    const table = useReactTable({
        data,
        columns,
        state: { sorting, columnVisibility: columnVisibility ?? {} },
        onSortingChange: isControlled
            ? (updater) => {
                  const next = typeof updater === 'function' ? updater(sorting) : updater;
                  onSortingChange(next);
              }
            : setInternalSorting,
        manualSorting: isControlled,
        getRowId: getRowId ? (row) => getRowId(row) : undefined,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: enableSorting && !isControlled ? getSortedRowModel() : undefined,
        meta,
    });

    return (
        <div className={`overflow-x-auto ${framed ? 'border border-mp-border rounded shadow' : ''} ${className}`}>
            <table className={`w-full text-sm ${columnWidths ? 'table-fixed' : ''}`}>
                <thead className="bg-mp-background border-b border-mp-border">
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHeaderCell
                                    key={header.id}
                                    header={header}
                                    tableSortable={enableSorting}
                                    width={columnWidths?.[header.column.id]}
                                />
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody className="bg-mp-surface divide-y divide-mp-border">
                    {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row) => {
                            const isExpanded =
                                getRowId !== undefined &&
                                expandedRowId != null &&
                                row.id === expandedRowId;
                            const isSelected =
                                getRowId !== undefined && selectedRowId != null && row.id === selectedRowId;
                            return (
                                <React.Fragment key={row.id}>
                                    <tr
                                        className={rowClassName(isSelected, isExpanded, !!onRowClick)}
                                        tabIndex={onRowClick ? 0 : undefined}
                                        aria-selected={isSelected}
                                        onKeyDown={
                                            onRowClick
                                                ? (event) => {
                                                      if (!isActivationKey(event)) return;
                                                      event.preventDefault();
                                                      onRowClick(row.original);
                                                  }
                                                : undefined
                                        }
                                        onClick={
                                            onRowClick
                                                ? (event) => {
                                                      if (
                                                          isIgnoredRowClick(
                                                              event.target,
                                                              event.currentTarget,
                                                          )
                                                      ) {
                                                          return;
                                                      }
                                                      onRowClick(row.original);
                                                  }
                                                : undefined
                                        }
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className="px-4 py-3 text-mp-foreground"
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                    {renderExpanded && isExpanded && (
                                        <tr>
                                            <td colSpan={table.getVisibleLeafColumns().length} className="px-4 py-4">
                                                {renderExpanded(row.original)}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <tr>
                            <td
                                colSpan={table.getVisibleLeafColumns().length}
                                className="px-4 py-8 text-center text-mp-muted"
                            >
                                <div className="flex flex-col items-center">
                                    <svg
                                        className="w-8 h-8 mb-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1.5}
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                    <p className="font-medium">
                                        {hasCustomEmpty ? emptyMessage : 'No data available'}
                                    </p>
                                    {!hasCustomEmpty && (
                                        <p className="text-sm">
                                            There are no items to display in this table.
                                        </p>
                                    )}
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};
